import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable, Subscription} from 'rxjs';
import {ChallengeBuilderProject, ChallengeTask} from 'src/app/core/model/project';
import {
  ChallengeScheduleAction,
  SetBackLoadingAction,
  SetNextLoadingAction,
  UpdateChallengeNextValidAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {MessageService} from 'src/app/services/message.service';
import {TasksService} from 'src/app/shared/services/tasks.service';
import {CbScheduleChipComponent, ScheduleChipType} from '../shared/cb-schedule-chip.component';
import {CbTaskIconButtonComponent} from '../shared/cb-task-icon-button/cb-task-icon-button.component';
import {GetProjectsAction} from 'src/app/core/store/projects/projects.action';

@Component({
  selector: 'cb-schedule',
  standalone: true,
  imports: [CommonModule, CbScheduleChipComponent, CbTaskIconButtonComponent],
  templateUrl: './cb-schedule.component.html',
  styleUrl: './cb-schedule.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbScheduleComponent implements OnInit {
  private nextClickSubscription?: Subscription;
  private backClickSubscription?: Subscription;
  private readonly store$ = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly tasksService = inject(TasksService);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;

  nextClick$ = input.required<Observable<void>>();
  backClick$ = input.required<Observable<void>>();
  cbProject = input<ChallengeBuilderProject>();
  step = input<string>();

  onNext = output<void>();
  onBack = output<void>();

  // Get selected tasks flattened from objectives
  selectedTasks = computed(() => {
    const objectives = this.cbProject()?.challengeBuilder?.objectives || [];
    const tasks: ChallengeTask[] = [];

    objectives.forEach(objective => {
      if (objective.tasks) {
        objective.tasks.forEach(task => {
          if (task.selected) {
            tasks.push(task);
          }
        });
      }
    });
    return tasks;
  });

  // Get cycle length to determine number of schedule items (3-7)
  cycleLength = computed(() => {
    const explicit = this.cbProject()?.challengeBuilder.cycleLength;
    if (explicit) return explicit;

    // Derive from first selected task's schedule length
    const tasks = this.selectedTasks();
    const scheduleLength = tasks.find(t => t.schedule?.length)?.schedule?.length;
    return scheduleLength || 7;
  });

  // Generate schedule labels based on cycle length
  scheduleLabels = computed(() => {
    const length = this.cycleLength();
    const labels: string[] = [];

    for (let i = 1; i <= length; i++) {
      labels.push(`Day ${i}`);
    }

    return labels;
  });

  // Track task icons by taskId
  taskIcons = signal<Record<string, string>>({});

  // Track task schedules - keyed by task _id
  taskSchedules = signal<Map<string, (ScheduleChipType | null)[]>>(new Map());

  // Computed that combines tasks with their schedules for template binding
  tasksWithSchedules = computed(() => {
    const tasks = this.selectedTasks();
    const schedules = this.taskSchedules();
    const labels = this.scheduleLabels();

    return tasks.map(task => ({
      task,
      schedule: labels.map((label, index) => ({
        label,
        type: schedules.get(task._id ?? '')?.[index] ?? null,
      })),
    }));
  });

  // Initialize schedules when tasks or project changes
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly initEffect = effect(
    () => {
      const tasks = this.selectedTasks();
      const length = this.cycleLength();
      const currentSchedules = this.taskSchedules();

      // No tasks selected, nothing to initialize
      if (tasks.length === 0) {
        return;
      }

      // Only initialize if we have no schedules yet, or if tasks changed
      const taskIds = new Set(tasks.map(t => t._id ?? ''));
      const currentIds = new Set(currentSchedules.keys());
      const needsInit =
        currentSchedules.size === 0 ||
        tasks.some(t => !currentSchedules.has(t._id ?? '')) ||
        [...currentIds].some(id => !taskIds.has(id));

      if (!needsInit) {
        return;
      }

      const schedules = new Map<string, (ScheduleChipType | null)[]>();

      tasks.forEach(task => {
        const taskId = task._id ?? '';
        // Preserve existing selections if available, otherwise initialize from project data
        const existingInState = currentSchedules.get(taskId);
        if (existingInState && existingInState.length === length) {
          schedules.set(taskId, existingInState);
          return;
        }

        // Initialize from project schedule or create empty schedule
        const existingSchedule = task.schedule || [];
        const schedule: (ScheduleChipType | null)[] = [];

        for (let i = 0; i < length; i++) {
          const scheduleValue = existingSchedule[i];
          schedule.push(scheduleValue ? (scheduleValue as ScheduleChipType) : null);
        }

        schedules.set(taskId, schedule);
      });

      this.taskSchedules.set(schedules);
      this.validateForm();

      // Initialize task icons if not yet set
      const cbProject = this.cbProject();
      if (cbProject && Object.keys(this.taskIcons()).length === 0) {
        this.initTaskIcons(cbProject);
      }
    },
    {allowSignalWrites: true}
  );

  // Validation status signal
  private readonly isFormValid = signal<boolean>(false);

  // Effect to update validation state
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly validationEffect = effect(
    () => {
      const isValid = this.isFormValid();
      this.store$.dispatch(new UpdateChallengeNextValidAction({valid: isValid}));
    },
    {allowSignalWrites: true}
  );

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.nextClickSubscription?.unsubscribe();
      this.backClickSubscription?.unsubscribe();
    });
  }

  ngOnInit(): void {
    // Subscribe to next button clicks from parent
    this.nextClickSubscription = this.nextClick$().subscribe(() => {
      this.onNextClick();
    });
    this.backClickSubscription = this.backClick$().subscribe(() => {
      this.onBackClick();
    });
  }

  private validateForm(): void {
    const schedules = this.taskSchedules();
    const tasks = this.selectedTasks();

    // Check if each task has at least one schedule selected
    const isValid = tasks.every(task => {
      const schedule = schedules.get(task._id ?? '') || [];
      return schedule.some(item => item !== null);
    });

    this.isFormValid.set(isValid);
  }

  onScheduleSelect(taskId: string, index: number, type: ScheduleChipType): void {
    this.taskSchedules.update(schedules => {
      const schedule = schedules.get(taskId);
      if (!schedule) return schedules;

      const newSchedule = [...schedule];
      newSchedule[index] = type;
      const newSchedules = new Map(schedules);
      newSchedules.set(taskId, newSchedule);
      return newSchedules;
    });
    this.validateForm();
  }

  getGoalName(): string {
    const goals = this.cbProject()?.challengeBuilder?.goals || [];
    const selectedGoal = goals.find(g => g.selected);
    return selectedGoal?.name || goals[0]?.name || 'Goal';
  }

  private initTaskIcons(cbProject: ChallengeBuilderProject): void {
    const objectives = cbProject.challengeBuilder?.objectives || [];
    const tasks = objectives.flatMap(obj => obj.tasks || []).filter(t => t.selected);
    const icons: Record<string, string> = {};

    for (const task of tasks) {
      if (!task._id) continue;
      if (task.icon) {
        icons[task._id] = task.icon;
      } else if (task.iconSuggestion) {
        const resolved = this.tasksService.findIconByKeyword(task.iconSuggestion);
        if (resolved) {
          icons[task._id] = resolved;
        }
      }
    }

    this.taskIcons.set(icons);
  }

  getTaskIcon(taskId: string): string {
    return this.taskIcons()[taskId] || '';
  }

  onTaskIconChange(taskId: string, icon: string): void {
    this.taskIcons.update(icons => ({...icons, [taskId]: icon}));
  }

  private onNextClick(): void {
    if (!this.isFormValid()) {
      this.messageService.add('Please select schedule times for each task', 'error');
      this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
      return;
    }

    this.saveSchedule(() => this.onNext.emit());
  }

  private onBackClick(): void {
    this.saveSchedule(() => this.onBack.emit());
  }

  private buildUpdatedTasks(): ChallengeTask[] {
    const schedules = this.taskSchedules();
    return this.selectedTasks().map(task => {
      const schedule = schedules.get(task._id ?? '') || [];
      const scheduleStrings = schedule.map(item => item || '');
      return {...task, schedule: scheduleStrings};
    });
  }

  private resetLoading(): void {
    this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
    this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
  }

  private saveSchedule(onSuccess?: () => void): void {
    this.store$.dispatch(
      new ChallengeScheduleAction({
        projectId: this.cbProject()?._id || '',
        tasks: this.buildUpdatedTasks(),
        onSuccess: () => {
          if (this.destroyed) return;
          onSuccess?.();
          this.resetLoading();
          this.store$.dispatch(new GetProjectsAction({}));
        },
        onFailure: (error: Error) => {
          if (this.destroyed) return;
          this.messageService.add('Failed to save schedule', 'error');
          console.error('Failed to save schedule:', error);
          this.resetLoading();
        },
      })
    );
  }
}
